import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ReactTable from 'react-table';
import { isNull, isUndefined } from 'lodash/lang';
import moment from 'moment';

import { FinanceCompleteFilterColumn } from 'components/pages/patient-source/shared/Worklist/WorklistTableHeaderFilter/WorklistFinanceCompleteFilter/FinanceCompleteFilterColumn';

import InvestigatorWorkListApi from '../../../../api/patient/InvestigatorWorkListApi';
import useAppInfo from '../../../../common/hooks/useAppInfo';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../constants/dateFormat';
import { CLOSED } from '../../../../constants/ssuStatuses';
import { MANAGE_PROGRESS_NOTES, VIEW_PROGRESS_NOTES } from '../../../../constants/userOperations';
import { ROLE_SYSTEM_ADMINISTRATOR } from '../../../../constants/userRoles';
import { pendoTrackDefaultSortingChange } from '../../../../pendo/PendoUtils';
import { userHasAccessTo, userHasRole } from '../../../../services/auth';
import { CellFormattedDate } from '../../../CellFormattedDate/CellFormattedDate';
import { generateUrlByKey } from '../../../root/router';
import { isLogCanBeLinked } from '../Patients/Logs/LogsPageServices';
import { AE, DV, ENCOUNTER_WIDGET } from '../shared/Worklist/constants';
import HighlightIcon from '../shared/Worklist/HighlightIcon';
import HighlightToolTip from '../shared/Worklist/HighlightToolTip';
import { NotesColumn } from '../shared/Worklist/NotesColumn';
import ReactTableHoc, {
  CustomSortHeader,
  resolveBasicCell,
  resolveUnsignedProcedureValue
} from '../shared/Worklist/ReactTableHoc/ReactTableHoc';
import { ReviewColumn } from '../shared/Worklist/ReviewColumn';
import {
  getUrlToPatientInfoPage,
  getUrlToReviewPage,
  resolveDomainCodeForReviewPages,
  resolveEmptyValue,
  resolveLogNameHeader
} from '../shared/Worklist/WorklistService';
import { EncounterFilterColumn } from '../shared/Worklist/WorklistTableHeaderFilter/WorklistEncounterFilter/EncounterFilterColumn';
import { SignedByFilterColumn } from '../shared/Worklist/WorklistTableHeaderFilter/WorklistSignedByFilter/SignedByFilterColumn';

import { LinkedSubjectsButton } from './LinkedSubjectsButton/LinkedSubjectsButton';
import {
  defaultInvestigatorFilters,
  investigatorAdverseEventsAndDeviationStatusesProvider,
  investigatorStatusesProvider,
  REVIEW_REQUIRED,
  WORKLIST_NAME
} from './constants';
import { InvestigatorWorklistContext } from './InvestigatorWorklistFilterContext';

import '../shared/Worklist/ReactTableHoc/ReactTableHoc.scss';

const getColumns = (
  selectedStatus,
  statusType,
  signedByFilterEnabled,
  groupsAndSignatures,
  isSignedByColumnFetching
) => {
  if (!selectedStatus) {
    return [];
  }
  if (statusType === ENCOUNTER_WIDGET) {
    return [
      {
        id: 'highlight',
        Header: (
          <HighlightToolTip>
            <span>Highlighted</span>
          </HighlightToolTip>
        ),
        accessor: 'highlight',
        sortable: false,
        className: 'rt-hoc-centered',
        Cell: ({ value }) => {
          return <HighlightIcon highlightType={value} />;
        },
        resizable: false,
        minWidth: 60,
        maxWidth: 100
      },
      {
        id: 'patientName',
        Header: <CustomSortHeader title={'Patient'} />,
        minWidth: 60,
        accessor({ patientFirstName, patientLastName }) {
          return `${patientFirstName} ${patientLastName}`;
        },
        Cell: ({ value, original }) => (
          <Link
            onClick={function(e) {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            to={getUrlToPatientInfoPage(original, WORKLIST_NAME)}
          >
            {value}
          </Link>
        )
      },
      {
        id: 'subjectId',
        Header: <CustomSortHeader title={'Subject ID'} />,
        Cell: ({ value }) => resolveBasicCell(value.subjectId || 'N/A'),
        accessor: d => d,
        minWidth: 60,
        sortMethod(a, b) {
          const aSubjectId = a.subjectId?.toUpperCase();
          const bSubjectId = b.subjectId?.toUpperCase();
          if (a.subjectId === b.subjectId) {
            return 0;
          }
          if (a.subjectId === null || aSubjectId > bSubjectId) {
            return 1;
          }
          if (b.subjectId === null || aSubjectId < bSubjectId) {
            return -1;
          }
        }
      },
      {
        Header: <CustomSortHeader title={'Study'} />,
        accessor: 'studyName',
        minWidth: 60,
        Cell: row => resolveBasicCell(row.original.studyName)
      },
      {
        Header: <CustomSortHeader title={'Site'} />,
        accessor: 'siteName',
        minWidth: 60,
        Cell: row => resolveBasicCell(row.original.siteName)
      },
      EncounterFilterColumn(
        InvestigatorWorklistContext,
        InvestigatorWorkListApi.getEncounterNamesAndStudyNamesBySsuIds,
        defaultInvestigatorFilters,
        investigatorStatusesProvider,
        'Investigator Worklist'
      ),
      {
        Header: <span className={'two-row-header'}>Form Review Required</span>,
        sortable: false,
        show: selectedStatus === REVIEW_REQUIRED,
        Cell: ({ original }) =>
          isSignedByColumnFetching ? (
            <div className={'signed-by-column-loader-gradient'} />
          ) : (
            <span className="tooltip-cell">{resolveUnsignedProcedureValue(original, groupsAndSignatures)}</span>
          ),
        className: 'rt-hoc-centered p-3',
        width: 135
      },
      {
        id: 'date',
        Header: <CustomSortHeader title={'Date'} />,
        width: 105,
        Cell: ({ original }) => {
          const date = original.piReviewCompleteDate || original.smReviewCompleteDate || original.visitDate;
          return resolveBasicCell(
            !isNull(date) && !isUndefined(date) ? moment.utc(date.toLocaleString()).format(DD_SLASH_MMM_SLASH_YYYY) : ''
          );
        },
        sortMethod: (a, b) => {
          return a.dateValue > b.dateValue ? -1 : 1;
        }
      },
      FinanceCompleteFilterColumn(InvestigatorWorklistContext),
      SignedByFilterColumn(
        InvestigatorWorklistContext,
        InvestigatorWorkListApi.getInvestigatorWorkListSignedByFilterSignersAndSmReviewers,
        investigatorStatusesProvider,
        signedByFilterEnabled,
        statusType,
        selectedStatus
      ),
      {
        Header: 'Review',
        accessor: 'review',
        sortable: false,
        className: 'rt-hoc-centered rt-hoc-pointer',
        width: 70,
        Cell: row => {
          const link = getUrlToReviewPage(row.original, 'Investigator Worklist');
          return <ReviewColumn reviewButtonDisabled={!link} link={link} />;
        }
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        sortable: false,
        className: 'rt-hoc-centered rt-hoc-pointer',
        width: 60,
        Cell: row => {
          const hasAccessToManage =
            userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
            (row.original.studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_PROGRESS_NOTES));
          return (
            <NotesColumn
              hasAccessToView={userHasAccessTo(VIEW_PROGRESS_NOTES)}
              hasAccessToEdit={hasAccessToManage}
              sitePatientIdentifier={row.original.ssuPatientId}
              studySiteStatus={row.original.studySiteStatus}
              notesInfo={row.original}
            />
          );
        }
      }
    ];
  } else {
    const domainCode = resolveDomainCodeForReviewPages(statusType);
    return [
      {
        id: 'reviewReason',
        Header: 'Review Reason',
        accessor: 'reviewReason',
        sortable: false,
        width: 180,
        Cell: row => resolveBasicCell(row.original.reviewReason),
        show: isLogCanBeLinked(domainCode) && selectedStatus === REVIEW_REQUIRED
      },
      {
        id: 'date',
        Header: <CustomSortHeader title={'Date'} />,
        accessor: 'date',
        width: 180,
        Cell: ({ value }) => (
          <span className="tooltip-cell">
            <CellFormattedDate date={value} defaultValue={'--'} format={'DD/MMM/YYYY, h:mm a'} toUpper={true} />
          </span>
        )
      },
      {
        id: 'logName',
        Header: <CustomSortHeader title={resolveLogNameHeader(statusType)} />,
        minWidth: 60,
        accessor: 'logName',
        Cell: ({ value, original }) => {
          const emptyValue = resolveEmptyValue(statusType);
          return (
            <Link
              data-testid="logName"
              onClick={function(e) {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
              to={getUrlToLogRow(original, domainCode)}
            >
              {value || emptyValue}
            </Link>
          );
        }
      },
      {
        id: 'patientName',
        Header: <CustomSortHeader title={'Patient'} />,
        minWidth: 60,
        accessor({ patientFirstName, patientLastName }) {
          return `${patientFirstName} ${patientLastName}`;
        },
        Cell: ({ value, original }) => {
          return original.linkedGroupId ? (
            <LinkedSubjectsButton row={original} worklistName={'Investigator Worklist'} />
          ) : (
            <Link
              onClick={function(e) {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
              to={getUrlToPatientInfoPage(original, 'Investigator Worklist')}
            >
              {value}
            </Link>
          );
        }
      },
      {
        id: 'subjectId',
        Header: <CustomSortHeader title={'Subject ID'} />,
        Cell: ({ value, original }) => {
          if (original.linkedGroupId) {
            return resolveBasicCell('Multiple');
          }
          return resolveBasicCell(value.subjectId || 'N/A');
        },
        accessor: d => d,
        minWidth: 60,
        sortMethod(a, b) {
          const aSubjectId = a.subjectId?.toUpperCase();
          const bSubjectId = b.subjectId?.toUpperCase();
          if (a.subjectId === b.subjectId) {
            return 0;
          }
          if (a.subjectId === null || aSubjectId > bSubjectId) {
            return 1;
          }
          if (b.subjectId === null || aSubjectId < bSubjectId) {
            return -1;
          }
        }
      },
      {
        Header: <CustomSortHeader title={'Study'} />,
        accessor: 'studyName',
        minWidth: 60,
        Cell: row => resolveBasicCell(row.original.studyName)
      },
      {
        Header: <CustomSortHeader title={'Site'} />,
        accessor: 'siteName',
        minWidth: 60,
        Cell: row => resolveBasicCell(row.original.siteName)
      },
      SignedByFilterColumn(
        InvestigatorWorklistContext,
        InvestigatorWorkListApi.getInvestigatorWorkListLogsSignedByFilterSignersAndSmReviewers,
        investigatorAdverseEventsAndDeviationStatusesProvider,
        signedByFilterEnabled,
        statusType
      ),
      {
        Header: 'Review',
        accessor: 'review',
        sortable: false,
        show: selectedStatus === REVIEW_REQUIRED,
        className: 'rt-hoc-centered rt-hoc-pointer',
        width: 70,
        Cell: row => {
          return <ReviewColumn link={getUrlToLogReview(row.original, domainCode)} reviewButtonDisabled={false} />;
        }
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        sortable: false,
        className: 'rt-hoc-centered rt-hoc-pointer',
        width: 60,
        Cell: row => {
          const hasAccessToManage =
            userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
            (row.original.studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_PROGRESS_NOTES));
          return (
            <NotesColumn
              hasAccessToView={userHasAccessTo(VIEW_PROGRESS_NOTES)}
              hasAccessToEdit={hasAccessToManage}
              sitePatientIdentifier={row.original.ssuPatientId}
              studySiteStatus={row.original.studySiteStatus}
              notesInfo={row.original}
            />
          );
        },
        show: !isLogCanBeLinked(domainCode)
      }
    ];
  }
};

export const InvestigatorWorklistTableNew = () => {
  const {
    page,
    pageSize,
    tableData,
    totalPages,
    statusType,
    setPageSize,
    setPage,
    sorted,
    setSorted,
    EncounterWidgetContext,
    AdverseEventsWidgetContext,
    DeviationWidgetContext,
    groupsAndSignatures,
    isSignedByColumnFetching
  } = useContext(InvestigatorWorklistContext);
  const appInfo = useAppInfo();
  const signedByFilterEnabled = appInfo?.features?.signedByFilterEnabled;
  const currentlySelected =
    EncounterWidgetContext?.currentlySelected ||
    AdverseEventsWidgetContext?.currentlySelected ||
    DeviationWidgetContext?.currentlySelected;
  return (
    <ReactTableHoc>
      <ReactTable
        onPageSizeChange={setPageSize}
        onPageChange={setPage}
        onSortedChange={sorted => {
          pendoTrackDefaultSortingChange(sorted);
          setSorted(sorted);
        }}
        columns={getColumns(
          currentlySelected,
          statusType,
          signedByFilterEnabled,
          groupsAndSignatures,
          isSignedByColumnFetching
        )}
        pages={totalPages}
        data={currentlySelected ? tableData : []}
        showPagination={!!currentlySelected}
        manual
        showPageSizeOptions
        minRows={1}
        pageSize={pageSize}
        page={page}
        noDataText={currentlySelected && 'No Records Found'}
        className="mx-4"
        defaultSorted={sorted}
      />
    </ReactTableHoc>
  );
};

function getUrlToLogRow(original, domainCode) {
  const { ssuPatientId, patientIdentifier, rowId, itemGroupTemplateId, itemGroupTemplateVersionId } = original;
  if (itemGroupTemplateId) {
    let key;
    switch (domainCode) {
      case AE:
        key = 'Investigator Worklist.Adverse Event Log Row';
        break;
      case DV:
        key = 'Investigator Worklist.Protocol Deviation Log Row';
        break;
      default:
        throw new Error(`Domain code "${domainCode}" is not supported`);
    }
    return generateUrlByKey(key, {
      ssuPatientId,
      patientId: patientIdentifier,
      patientItemGroupId: itemGroupTemplateId,
      itemGroupTemplateVersionId,
      rowId
    });
  }
}

function getUrlToLogReview(original, domainCode) {
  const { patientIdentifier, ssuPatientId, itemGroupTemplateId } = original;
  if (itemGroupTemplateId) {
    let key;
    switch (domainCode) {
      case AE:
        key = 'Investigator Worklist.Adverse Event Log Review';
        break;
      case DV:
        key = 'Investigator Worklist.Protocol Deviation Log Review';
        break;
      default:
        throw new Error(`Domain code "${domainCode}" is not supported`);
    }
    return generateUrlByKey(key, {
      patientId: patientIdentifier,
      ssuPatientId: ssuPatientId,
      patientItemGroupId: itemGroupTemplateId
    });
  }
}
